import { useState } from "react"

import { useTranslation } from "react-i18next"

import {
  downloadHostedAgentArtifact,
  type HostedAgentArtifact,
} from "@/api/client/hosted-agents-artifacts"
import { Button } from "@/components/ui/button"

export function ArtifactDocument({
  artifact,
  agentSlug,
}: {
  artifact: HostedAgentArtifact
  agentSlug: string
}) {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const download = async () => {
    setBusy(true)
    setFailed(false)
    try {
      const blob = await downloadHostedAgentArtifact(agentSlug, artifact.id)
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement("a")
      anchor.href = url
      anchor.download =
        artifact.blob_ref?.filename || artifact.blob_ref?.path.split("/").at(-1) || artifact.title
      anchor.click()
      // Give the browser time to start the download before releasing its local URL.
      window.setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch {
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="space-y-4">
      {artifact.content ? (
        <pre className="font-sans text-sm break-words whitespace-pre-wrap">{artifact.content}</pre>
      ) : null}
      <p className="text-muted-foreground text-sm">
        {artifact.blob_ref?.filename || artifact.title}
      </p>
      <Button disabled={busy || !artifact.blob_ref} type="button" onClick={() => void download()}>
        {t("agents.artifacts.download_document", { defaultValue: "Download document" })}
      </Button>
      {failed ? (
        <p className="text-sm" role="alert">
          {t("agents.artifacts.download_failed", {
            defaultValue: "The document could not be downloaded. Check your access and try again.",
          })}
        </p>
      ) : null}
    </div>
  )
}
